import type { EditorState } from "../../vendor/src/features/remotion-editor/editor/state/types";
import { itemOwner } from "./group-split";
import { placeTimelineSelection, type PlacementResult } from "./timeline-place";
import { resizeTimelineItem } from "./timeline-resize";

export function applyTimingInput(
  state: EditorState,
  primaryId: string,
  ids: readonly string[],
  field: "start" | "duration",
  value: number,
): PlacementResult {
  if (!Number.isSafeInteger(value) || value < (field === "duration" ? 1 : 0))
    return { ok: false, state, reason: "invalid-position" };
  if (field === "start")
    return placeTimelineSelection(state, ids, primaryId, value);
  const primary = state.undoableState.items[primaryId],
    owner = itemOwner(state, primaryId);
  if (!primary || !owner) return { ok: false, state, reason: "missing-item" };
  if (owner.track.locked) return { ok: false, state, reason: "locked" };
  const sync =
    state.isLinkingEnabled &&
    !owner.parentId &&
    owner.track.metadata?.isMainTrack &&
    owner.track.metadata.type === "video";
  const targets = [
    ...new Set(ids.includes(primaryId) ? ids : [primaryId]),
  ].filter((id) => {
    const own = itemOwner(state, id);
    return (
      own &&
      !own.track.locked &&
      (!sync ||
        (!own.parentId &&
          own.track.metadata?.isMainTrack &&
          own.track.metadata.type === "video"))
    );
  });
  let next = sync ? state : { ...state, isLinkingEnabled: false };
  for (const id of targets) {
    // A selected group owns its children; applying the same duration again would expand a trimmed child.
    let parent = itemOwner(state, id)?.parentId;
    let selectedAncestor = false;
    while (parent) {
      if (targets.includes(parent)) {
        selectedAncestor = true;
        break;
      }
      parent = itemOwner(state, parent)?.parentId;
    }
    if (selectedAncestor) continue;
    const item = next.undoableState.items[id];
    if (item) next = resizeTimelineItem(next, id, "right", item.from + value);
  }
  if (next.undoableState === state.undoableState) return { ok: true, state };
  return {
    ok: true,
    state: {
      ...next,
      isLinkingEnabled: state.isLinkingEnabled,
      selectedItems: state.selectedItems.filter(
        (id) => next.undoableState.items[id],
      ),
    },
  };
}
